/**
 * Types nominaux des grandeurs physiques.
 *
 * Le document mêle en permanence mètres et kilomètres, secondes et minutes.
 * Les confondre est la faute la plus probable du projet : chaque grandeur
 * porte donc un type distinct, et aucune conversion implicite n'est possible.
 *
 * Toutes les grandeurs internes sont en unités de base : mètres, secondes,
 * kilomètres par heure, degrés décimaux. Les conversions d'affichage vivent
 * exclusivement dans `ui/` et appliquent EF-CAL-008.
 */

declare const brand: unique symbol;

type Brand<T, B> = T & { readonly [brand]: B };

export type Meters = Brand<number, 'Meters'>;
export type Seconds = Brand<number, 'Seconds'>;
export type KmH = Brand<number, 'KmH'>;
export type Degrees = Brand<number, 'Degrees'>;
export type Radians = Brand<number, 'Radians'>;
export type Percent = Brand<number, 'Percent'>;

export const meters = (value: number): Meters => value as Meters;
export const seconds = (value: number): Seconds => value as Seconds;
export const kmh = (value: number): KmH => value as KmH;
export const degrees = (value: number): Degrees => value as Degrees;
export const radians = (value: number): Radians => value as Radians;
export const percent = (value: number): Percent => value as Percent;

/** Retire le type nominal. Réservé aux boucles numériques internes. */
export const raw = (value: number): number => value;

export const DEG_TO_RAD = Math.PI / 180;
export const RAD_TO_DEG = 180 / Math.PI;

export const toRadians = (value: Degrees): Radians => radians(value * DEG_TO_RAD);
export const toDegrees = (value: Radians): Degrees => degrees(value * RAD_TO_DEG);

/**
 * Vitesse en km/h vers mètres par seconde.
 *
 * Accepte un nombre nu : les vitesses proviennent des paramètres, eux-mêmes
 * issus du JSON de l'annexe B, et ne traversent jamais une frontière où une
 * confusion d'unité serait possible.
 */
export const kmhToMs = (kilometersPerHour: number): number => (kilometersPerHour * 1000) / 3600;
